import {
  Badge,
  Box,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Radio,
  RadioGroup,
  Text,
} from "@chakra-ui/react";
import { phoneFormatter } from "../../../../../../shared/utils/phone-formatter";
import AddressAutocompleteInput from "../../../../../office-branch/components/AddressAutocompleteInput";
import {
  EntityProfileContactCardShell,
  EntityProfileContactGrid,
  EntityProfileContactInputField,
  EntityProfileContactPhoneField,
} from "../../../../../entityProfileModal/components/EntityProfileContactCard";
import {
  entityProfileModalBorders,
  entityProfileModalColors,
} from "../../../../../entityProfileModal/entityProfileModal.theme";
import type { PatientProfileBridgeActions } from "../../../../patientProfileModal.actions";
import { formatPatientProfileValue } from "../../../../patientProfileModal.utils";
import type { EmergencyContact } from "../emergencyPreparedness.types";
import { isInvalidEmergencyContactEmail } from "../emergencyPreparedness.utils";
import EntityModalButton from "../../../../../entityProfileModal/components/EntityModalButton";

function normalizeComparablePhoneNumber(phoneNumber: string | null | undefined) {
  if (phoneNumber === null || phoneNumber === undefined || phoneNumber.trim() === "") {
    return null;
  }

  return phoneFormatter.isValidNumber(phoneNumber)
    ? phoneFormatter.formatE164(phoneNumber)
    : phoneNumber;
}

function isMainPhoneNumber(phoneNumber: string | null | undefined, mainPhoneNumber: string | null) {
  const normalizedPhoneNumber = normalizeComparablePhoneNumber(phoneNumber);
  const normalizedMainPhoneNumber = normalizeComparablePhoneNumber(mainPhoneNumber);

  return (
    normalizedPhoneNumber !== null &&
    normalizedMainPhoneNumber !== null &&
    normalizedPhoneNumber === normalizedMainPhoneNumber
  );
}

function getMainPhoneNumberValue(phoneNumber: string | null | undefined) {
  if (phoneNumber === null || phoneNumber === undefined || phoneNumber.trim() === "") {
    return null;
  }

  return phoneFormatter.isValidNumber(phoneNumber) ? phoneFormatter.formatE164(phoneNumber) : null;
}

function MainPhoneCheckbox(props: {
  isDisabled: boolean;
  label: string;
  mainPhoneNumber: string | null;
  phoneNumber: string | null | undefined;
  onUpdateMainPhoneNumber: (phoneNumber: string | null) => void;
}) {
  const mainPhoneNumberValue = getMainPhoneNumberValue(props.phoneNumber);

  return (
    <Checkbox
      isChecked={isMainPhoneNumber(props.phoneNumber, props.mainPhoneNumber)}
      isDisabled={props.isDisabled || mainPhoneNumberValue === null}
      size="sm"
      onChange={() => props.onUpdateMainPhoneNumber(mainPhoneNumberValue)}
    >
      {props.label}
    </Checkbox>
  );
}

function EmergencyPhoneValue(props: {
  actions: PatientProfileBridgeActions;
  isOnboardingAgency: boolean;
  label: string;
  mainPhoneNumber: string | null;
  phoneNumber: string | null | undefined;
}) {
  if (props.phoneNumber === null || props.phoneNumber === undefined || props.phoneNumber === "") {
    return <>{formatPatientProfileValue(props.phoneNumber)}</>;
  }

  return (
    <HStack flexWrap="wrap" spacing={1}>
      <span>{formatPatientProfileValue(props.phoneNumber)}</span>
      {props.isOnboardingAgency ? (
        <>
          <EntityModalButton
            isIconOnly
            aria-label={`Call ${props.label}`}
            iconClassName="fa fa-phone"
            kind="white"
            size="xs"
            title="Call"
            onClick={() => props.actions.openDialer({ predefinedPhoneNumber: props.phoneNumber! })}
          />
          {isMainPhoneNumber(props.phoneNumber, props.mainPhoneNumber) ? (
            <Badge colorScheme="blue" fontSize="10px" lineHeight="1.2">
              Main
            </Badge>
          ) : null}
        </>
      ) : null}
    </HStack>
  );
}

function EmergencyContactField(props: { label: string; value: React.ReactNode }) {
  return (
    <Box>
      <Text
        color={entityProfileModalColors.labelText}
        fontSize="12px"
        fontWeight={700}
        letterSpacing="0.02em"
        textTransform="uppercase"
      >
        {props.label}
      </Text>
      <Box color={entityProfileModalColors.text} fontSize="14px" lineHeight="1.35" minH="19px">
        {props.value}
      </Box>
    </Box>
  );
}

function EmergencyAddressAutocompleteField(props: {
  address: string;
  isDisabled: boolean;
  onChange: (address: string) => void;
}) {
  return (
    <FormControl>
      <FormLabel fontSize="13px" fontWeight={700}>
        Address
      </FormLabel>
      <AddressAutocompleteInput
        addressText={props.address}
        isDisabled={props.isDisabled}
        value={null}
        onChange={(address) => props.onChange(address)}
        onClear={() => props.onChange("")}
      />
    </FormControl>
  );
}

export function EmergencyContactCard(props: {
  actions: PatientProfileBridgeActions;
  contact: EmergencyContact;
  index: number;
  isOnboardingAgency: boolean;
  mainPhoneNumber: string | null;
}) {
  const { contact } = props;

  return (
    <EntityProfileContactCardShell title={`Emergency Contact #${props.index + 1}`}>
      <EntityProfileContactGrid columns={2}>
        <EmergencyContactField
          label="First Name"
          value={formatPatientProfileValue(contact.firstName)}
        />
        <EmergencyContactField
          label="Last Name"
          value={formatPatientProfileValue(contact.lastName)}
        />
        {props.isOnboardingAgency ? null : (
          <EmergencyContactField
            label="Lives With Patient"
            value={formatPatientProfileValue(contact.livesWithPatient)}
          />
        )}
        <EmergencyContactField
          label="Relationship"
          value={formatPatientProfileValue(contact.relationship)}
        />
        <EmergencyContactField
          label={props.isOnboardingAgency ? "Phone" : "Phone 1"}
          value={
            <EmergencyPhoneValue
              actions={props.actions}
              isOnboardingAgency={props.isOnboardingAgency}
              label="emergency phone 1"
              mainPhoneNumber={props.mainPhoneNumber}
              phoneNumber={contact.emergencyPhoneNumber1}
            />
          }
        />
        {props.isOnboardingAgency ? (
          <EmergencyContactField
            label="Email"
            value={formatPatientProfileValue(contact.email ?? null)}
          />
        ) : (
          <>
            <EmergencyContactField
              label="Phone 2"
              value={
                <EmergencyPhoneValue
                  actions={props.actions}
                  isOnboardingAgency={props.isOnboardingAgency}
                  label="emergency phone 2"
                  mainPhoneNumber={props.mainPhoneNumber}
                  phoneNumber={contact.emergencyPhoneNumber2}
                />
              }
            />
            <EmergencyContactField
              label="Address"
              value={formatPatientProfileValue(contact.address)}
            />
          </>
        )}
      </EntityProfileContactGrid>
    </EntityProfileContactCardShell>
  );
}

export function EmergencyContactEditorCard(props: {
  contact: EmergencyContact;
  index: number;
  isDisabled: boolean;
  isOnboardingAgency: boolean;
  mainPhoneNumber: string | null;
  showErrors: boolean;
  onChange: (index: number, contact: EmergencyContact) => void;
  onRemove: (index: number) => void;
  onUpdateMainPhoneNumber: (phoneNumber: string | null) => void;
}) {
  const updateField = <TKey extends keyof EmergencyContact>(
    key: TKey,
    value: EmergencyContact[TKey]
  ) => {
    props.onChange(props.index, { ...props.contact, [key]: value });
  };

  return (
    <EntityProfileContactCardShell
      isRemoveDisabled={props.isDisabled}
      removeLabel="Remove emergency contact"
      title={`Emergency Contact #${props.index + 1}`}
      variant="editing"
      onRemove={() => props.onRemove(props.index)}
    >
      <EntityProfileContactGrid columns={2}>
        <EntityProfileContactInputField
          errorMessage="First name is required."
          isDisabled={props.isDisabled}
          isInvalid={props.showErrors && props.contact.firstName.trim() === ""}
          isRequired={true}
          label="First Name"
          value={props.contact.firstName}
          onChange={(value) => updateField("firstName", value)}
        />
        <EntityProfileContactInputField
          errorMessage="Last name is required."
          isDisabled={props.isDisabled}
          isInvalid={props.showErrors && props.contact.lastName.trim() === ""}
          isRequired={true}
          label="Last Name"
          value={props.contact.lastName}
          onChange={(value) => updateField("lastName", value)}
        />
        {props.isOnboardingAgency ? null : (
          <FormControl
            isRequired
            isInvalid={props.showErrors && props.contact.livesWithPatient === undefined}
          >
            <FormLabel fontSize="13px" fontWeight={700}>
              Lives With Patient
            </FormLabel>
            <RadioGroup
              value={
                props.contact.livesWithPatient === undefined
                  ? ""
                  : props.contact.livesWithPatient
                  ? "true"
                  : "false"
              }
              onChange={(value) => updateField("livesWithPatient", value === "true")}
            >
              <HStack minH="40px" spacing={4}>
                <Radio isDisabled={props.isDisabled} value="true">
                  Yes
                </Radio>
                <Radio isDisabled={props.isDisabled} value="false">
                  No
                </Radio>
              </HStack>
            </RadioGroup>
            <FormErrorMessage>Select whether this contact lives with the patient.</FormErrorMessage>
          </FormControl>
        )}
        <EntityProfileContactInputField
          isDisabled={props.isDisabled}
          label="Relationship"
          value={props.contact.relationship}
          onChange={(value) => updateField("relationship", value)}
        />
        {props.isOnboardingAgency ? (
          <EntityProfileContactInputField
            errorMessage="Enter a valid email address."
            isDisabled={props.isDisabled}
            isInvalid={props.showErrors && isInvalidEmergencyContactEmail(props.contact.email)}
            label="Email"
            value={props.contact.email ?? ""}
            onChange={(value) => updateField("email", value)}
          />
        ) : null}
        <EntityProfileContactPhoneField
          isDisabled={props.isDisabled}
          isRequired={true}
          label={props.isOnboardingAgency ? "Phone" : "Phone 1"}
          showErrors={props.showErrors}
          value={props.contact.emergencyPhoneNumber1 ?? ""}
          onChange={(value) => updateField("emergencyPhoneNumber1", value)}
        />
        {props.isOnboardingAgency ? (
          <Box alignSelf="center">
            <MainPhoneCheckbox
              isDisabled={props.isDisabled}
              label="Main phone 1"
              mainPhoneNumber={props.mainPhoneNumber}
              phoneNumber={props.contact.emergencyPhoneNumber1}
              onUpdateMainPhoneNumber={props.onUpdateMainPhoneNumber}
            />
          </Box>
        ) : null}
        {props.isOnboardingAgency ? null : (
          <>
            <EntityProfileContactPhoneField
              isDisabled={props.isDisabled}
              label="Phone 2"
              showErrors={props.showErrors}
              value={props.contact.emergencyPhoneNumber2 ?? ""}
              onChange={(value) => updateField("emergencyPhoneNumber2", value)}
            />
            <EmergencyAddressAutocompleteField
              address={props.contact.address ?? ""}
              isDisabled={props.isDisabled}
              onChange={(value) => updateField("address", value)}
            />
          </>
        )}
      </EntityProfileContactGrid>
    </EntityProfileContactCardShell>
  );
}

export function AddEmergencyContactButton(props: { isDisabled: boolean; onClick: () => void }) {
  return (
    <EntityModalButton
      disabled={props.isDisabled}
      iconClassName="fa fa-plus"
      kind="blue"
      size="sm"
      variant="outline"
      onClick={props.onClick}
    >
      Add Emergency Contact
    </EntityModalButton>
  );
}

export function NoEmergencyContactsRow(props: {
  isDisabled: boolean;
  isEditing: boolean;
  onAdd: () => void;
}) {
  return (
    <HStack
      borderBottom={entityProfileModalBorders.default}
      gridColumn="1 / -1"
      justifyContent="space-between"
      minH="56px"
    >
      <Text color={entityProfileModalColors.secondaryText} fontSize="14px" fontWeight={700}>
        No emergency contacts
      </Text>
      <Box minW="186px" textAlign="right">
        {props.isEditing ? (
          <AddEmergencyContactButton isDisabled={props.isDisabled} onClick={props.onAdd} />
        ) : null}
      </Box>
    </HStack>
  );
}
